import { useEffect, useState, useMemo } from "react";
import { 
  Calendar as CalendarIcon, Clock, ChevronLeft, ChevronRight, 
  CheckCircle2, XCircle, RefreshCw, CalendarDays, Ban, RotateCcw,
  User, Filter
} from "lucide-react";
import { API, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { toast } from "@/components/ui/sonner";
import { getStaffColor } from "@/utils/staffColors";

// Utilitaire d'extraction de date locale YYYY-MM-DD
function getLocalDateString(val) {
  if (!val) return "";
  const d = new Date(val);
  if (isNaN(d.getTime())) {
    if (typeof val === "string") return val.substring(0, 10);
    return "";
  }
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}

// Utilitaire d'extraction d'heure locale HH:MM
function getLocalTimeString(val) {
  if (!val) return "--:--";
  const d = new Date(val);
  if (isNaN(d.getTime())) {
    if (typeof val === "string" && val.includes("T")) {
      return val.split("T")[1].substring(0, 5);
    }
    return typeof val === "string" ? val.substring(0, 5) : "--:--";
  }
  return d.toLocaleTimeString("fr-FR", { hour: "2-digit", minute: "2-digit" });
}

// Dialogue de détails agrandi avec les 4 actions
export function BookingDetailsDialog({ booking, open, onOpenChange, onClose, onUpdated, onStatusChange }) {
  const isOpen = open !== undefined ? open : Boolean(booking);
  const handleClose = () => {
    if (onOpenChange) onOpenChange(false);
    if (onClose) onClose();
  };

  if (!booking) return null;
  const rawDate = booking.start_at || booking.start_time || booking.booking_date || booking.start;

  const handleUpdate = (status) => {
    if (onStatusChange) onStatusChange(booking.id, status);
    if (onUpdated) onUpdated();
    handleClose();
  };

  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-lg p-6">
        <DialogHeader>
          <DialogTitle className="text-lg font-bold text-slate-900">
            Détails du rendez-vous
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-5 pt-3 text-sm text-slate-700">
          <div className="rounded-2xl border border-slate-200/80 bg-slate-50/80 p-5 space-y-3">
            <div className="flex justify-between items-center pb-2 border-b border-slate-200/60">
              <span className="text-xs font-semibold uppercase tracking-wider text-slate-500">Prestation</span>
              <strong className="text-base text-slate-900">{booking.service_name || booking.service?.name || "Prestation"}</strong>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-slate-500">Client :</span>
              <strong className="text-slate-900 text-sm">{booking.customer_name || booking.customer?.name || "Client"}</strong>
            </div>
            {(booking.customer_phone || booking.customer?.phone) && (
              <div className="flex justify-between items-center">
                <span className="text-slate-500">Téléphone :</span>
                <a href={`tel:${booking.customer_phone || booking.customer?.phone}`} className="text-blue-600 underline font-semibold">
                  {booking.customer_phone || booking.customer?.phone}
                </a>
              </div>
            )}
            <div className="flex justify-between items-center">
              <span className="text-slate-500">Date & heure :</span>
              <span className="font-medium text-slate-800">{rawDate ? new Date(rawDate).toLocaleString("fr-FR") : "--"}</span>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-slate-500">Statut actuel :</span>
              <span className="font-semibold text-xs text-slate-700 uppercase">
                {booking.status === "completed" ? "Honoré" : booking.status === "no_show" ? "No-show" : booking.status === "cancelled" ? "Annulé" : "En attente / Confirmé"}
              </span>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-2 pt-1">
            <Button
              variant="outline"
              className="h-10 text-xs font-semibold text-emerald-700 border-emerald-200 hover:bg-emerald-50"
              onClick={() => handleUpdate(booking.status === "completed" ? "confirmed" : "completed")}
            >
              <CheckCircle2 className="mr-1.5 h-4 w-4" />
              {booking.status === "completed" ? "Remettre en attente" : "Marquer Honoré"}
            </Button>
            <Button
              variant="outline"
              className="h-10 text-xs font-semibold text-rose-700 border-rose-200 hover:bg-rose-50"
              onClick={() => handleUpdate(booking.status === "no_show" ? "confirmed" : "no_show")}
            >
              <XCircle className="mr-1.5 h-4 w-4" />
              {booking.status === "no_show" ? "Remettre en attente" : "Marquer No-show"}
            </Button>
            <Button
              variant="outline"
              className="h-10 text-xs font-semibold text-slate-600 border-slate-200 hover:bg-slate-100"
              onClick={() => handleUpdate("cancelled")}
            >
              <Ban className="mr-1.5 h-4 w-4" /> Annuler le RDV
            </Button>
            <Button
              variant="outline"
              className="h-10 text-xs font-semibold text-blue-600 border-blue-200 hover:bg-blue-50"
              onClick={() => handleUpdate("confirmed")}
            >
              <RotateCcw className="mr-1.5 h-4 w-4" /> Remettre en attente
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

export default function CalendarPage() {
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(true);

  // Gestion des équipiers et du filtre
  const [employees, setEmployees] = useState([]);
  const [selectedStaffFilter, setSelectedStaffFilter] = useState("all");

  const [currentDate, setCurrentDate] = useState(new Date());
  const [selectedDateStr, setSelectedDateStr] = useState(null);
  const [viewMode, setViewMode] = useState("month");
  const [selectedBookingForModal, setSelectedBookingForModal] = useState(null);

  const loadBookings = () => {
    setLoading(true);
    API.get("/bookings")
      .then((res) => setBookings(res.data || []))
      .catch((err) => toast.error(formatApiError(err)))
      .finally(() => setLoading(false));
  };

  const loadEmployees = () => {
    API.get("/employees")
      .then((res) => setEmployees(res.data || []))
      .catch((err) => console.error("Impossible de charger les employés :", err));
  };

  useEffect(() => {
    loadBookings();
    loadEmployees();
  }, []);

  // Liste filtrée globale utilisée par TOUTES les vues
  const filteredBookings = useMemo(() => {
    if (selectedStaffFilter === "all") return bookings;
    if (selectedStaffFilter === "unassigned") {
      return bookings.filter((b) => !b.employee_id && !b.employee?.id);
    }
    return bookings.filter((b) => {
      const empId = b.employee_id || b.employee?.id;
      return String(empId) === String(selectedStaffFilter);
    });
  }, [bookings, selectedStaffFilter]);

  const todayStr = getLocalDateString(new Date());

  const handlePrev = () => {
    const d = new Date(currentDate);
    if (viewMode === "month") d.setMonth(d.getMonth() - 1);
    else if (viewMode === "week") d.setDate(d.getDate() - 7);
    else d.setDate(d.getDate() - 1);
    setCurrentDate(d);
  };

  const handleNext = () => {
    const d = new Date(currentDate);
    if (viewMode === "month") d.setMonth(d.getMonth() + 1);
    else if (viewMode === "week") d.setDate(d.getDate() + 7);
    else d.setDate(d.getDate() + 1);
    setCurrentDate(d);
  };

  const handleToday = () => {
    const now = new Date();
    setCurrentDate(now);
    setSelectedDateStr(getLocalDateString(now));
  };

  const updateBookingStatus = async (bookingId, targetStatus) => {
    try {
      const current = bookings.find((b) => b.id === bookingId);
      const newStatus = current?.status === targetStatus ? "confirmed" : targetStatus;

      await API.patch(`/bookings/${bookingId}`, { status: newStatus });
      setBookings((prev) =>
        prev.map((b) => (b.id === bookingId ? { ...b, status: newStatus } : b))
      );

      window.dispatchEvent(new Event("bookings-changed"));

      if (newStatus === "completed") {
        toast.success("Rendez-vous marqué comme honoré (CA encaissé)");
      } else if (newStatus === "no_show") {
        toast.error("Rendez-vous marqué comme non honoré (No-show)");
      } else if (newStatus === "cancelled") {
        toast.info("Rendez-vous annulé");
      } else {
        toast.info("Rendez-vous remis en attente");
      }
    } catch (err) {
      toast.error(formatApiError(err, "Erreur mise à jour statut"));
    }
  };

  // Indexation par date calculée SUR LA LISTE FILTRÉE
  const bookingsByDate = useMemo(() => {
    const map = {};
    filteredBookings.forEach((b) => {
      const rawDate = b.start_at || b.start_time || b.booking_date || b.start || b.date;
      const d = getLocalDateString(rawDate);
      if (d) {
        if (!map[d]) map[d] = [];
        map[d].push(b);
      }
    });
    return map;
  }, [filteredBookings]);

  // Panneau latéral calculé SUR LA LISTE FILTRÉE
  const sidePanelBookings = useMemo(() => {
    if (selectedDateStr) {
      return (bookingsByDate[selectedDateStr] || []).sort((a, b) => {
        const aRaw = a.start_at || a.start_time || a.start || "";
        const bRaw = b.start_at || b.start_time || b.start || "";
        return aRaw.localeCompare(bRaw);
      });
    }

    const now = new Date();
    now.setHours(0, 0, 0, 0);

    return [...filteredBookings]
      .filter((b) => {
        const raw = b.start_at || b.start_time || b.booking_date || b.start;
        return raw && new Date(raw) >= now;
      })
      .sort((a, b) => {
        const aRaw = a.start_at || a.start_time || a.start || "";
        const bRaw = b.start_at || b.start_time || b.start || "";
        return aRaw.localeCompare(bRaw);
      })
      .slice(0, 15);
  }, [filteredBookings, selectedDateStr, bookingsByDate]);

  const monthGridDays = useMemo(() => {
    const year = currentDate.getFullYear();
    const month = currentDate.getMonth();

    const firstDayOfMonth = new Date(year, month, 1);
    const lastDayOfMonth = new Date(year, month + 1, 0);

    let startDayOfWeek = firstDayOfMonth.getDay() - 1;
    if (startDayOfWeek === -1) startDayOfWeek = 6;

    const days = [];

    const prevMonthLastDay = new Date(year, month, 0).getDate();
    for (let i = startDayOfWeek - 1; i >= 0; i--) {
      const d = new Date(year, month - 1, prevMonthLastDay - i);
      days.push({ date: d, isCurrentMonth: false });
    }

    for (let i = 1; i <= lastDayOfMonth.getDate(); i++) {
      const d = new Date(year, month, i);
      days.push({ date: d, isCurrentMonth: true });
    }

    const remaining = (7 - (days.length % 7)) % 7;
    for (let i = 1; i <= remaining; i++) {
      const d = new Date(year, month + 1, i);
      days.push({ date: d, isCurrentMonth: false });
    }

    return days;
  }, [currentDate]);

  const weekDays = useMemo(() => {
    const d = new Date(currentDate);
    const day = d.getDay();
    const diff = d.getDate() - day + (day === 0 ? -6 : 1);
    const monday = new Date(d.setDate(diff));

    const arr = [];
    for (let i = 0; i < 7; i++) {
      const nextDay = new Date(monday);
      nextDay.setDate(monday.getDate() + i);
      arr.push(nextDay);
    }
    return arr;
  }, [currentDate]);

  const viewTitle = useMemo(() => {
    if (viewMode === "month") {
      return currentDate.toLocaleDateString("fr-FR", { month: "long", year: "numeric" });
    }
    if (viewMode === "week") {
      const start = weekDays[0].toLocaleDateString("fr-FR", { day: "numeric", month: "short" });
      const end = weekDays[6].toLocaleDateString("fr-FR", { day: "numeric", month: "short", year: "numeric" });
      return `Du ${start} au ${end}`;
    }
    return currentDate.toLocaleDateString("fr-FR", { weekday: "short", day: "numeric", month: "short", year: "numeric" });
  }, [currentDate, viewMode, weekDays]);

  return (
    <div className="space-y-6">
      {/* En-tête */}
      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="font-display text-2xl font-bold text-slate-900">Calendrier & Planning</h1>
          <p className="mt-0.5 text-sm text-slate-500">
            Navigation temporelle et émargement des créneaux (honoré, no-show, annulation).
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" size="icon" onClick={loadBookings}>
            <RefreshCw className="h-4 w-4 text-slate-600" />
          </Button>
        </div>
      </div>

      {/* Barre de navigation & Filtres */}
      <div className="flex flex-wrap items-center justify-between gap-3 rounded-2xl border border-slate-200 bg-white p-3.5 shadow-sm">
        <div className="flex flex-wrap items-center gap-2 sm:gap-3">
          <Button
            variant="outline"
            size="sm"
            onClick={handleToday}
            className="h-8 font-semibold text-xs border-slate-300 hover:bg-slate-50"
          >
            Aujourd'hui
          </Button>
          <div className="flex items-center">
            <Button variant="ghost" size="icon" className="h-8 w-8 text-slate-600" onClick={handlePrev}>
              <ChevronLeft className="h-4 w-4" />
            </Button>
            <Button variant="ghost" size="icon" className="h-8 w-8 text-slate-600" onClick={handleNext}>
              <ChevronRight className="h-4 w-4" />
            </Button>
          </div>

          <div className="w-[190px] sm:w-[220px] text-left">
            <span className="font-display text-sm sm:text-base font-bold text-slate-900 capitalize block truncate">
              {viewTitle}
            </span>
          </div>

          <div className="flex rounded-lg border border-slate-200 bg-slate-100/80 p-0.5 shadow-xs text-xs font-semibold shrink-0">
            {[
              { id: "month", label: "Mois" },
              { id: "week", label: "Semaine" },
              { id: "day", label: "Jour" },
            ].map((m) => (
              <button
                key={m.id}
                onClick={() => setViewMode(m.id)}
                className={`rounded-md px-3 py-1 transition-all ${
                  viewMode === m.id
                    ? "bg-white text-slate-900 shadow-xs font-bold"
                    : "text-slate-600 hover:text-slate-900"
                }`}
              >
                {m.label}
              </button>
            ))}
          </div>

          {/* Filtre par employé */}
          <div className="flex items-center gap-1.5 ml-1">
            <select
              value={selectedStaffFilter}
              onChange={(e) => setSelectedStaffFilter(e.target.value)}
              className="h-8 rounded-lg border border-slate-200 bg-slate-50/80 px-2.5 text-xs font-medium text-slate-700 hover:bg-slate-100 focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500 transition-colors cursor-pointer"
            >
              <option value="all">👥 Tous les équipiers</option>
              {employees.map((emp) => (
                <option key={emp.id} value={emp.id}>
                  👤 {emp.name}
                </option>
              ))}
              <option value="unassigned">⚪ Non assigné</option>
            </select>
          </div>
        </div>

        {/* Légende des statuts */}
        <div className="flex items-center gap-3 text-xs">
          <span className="flex items-center gap-1.5 text-slate-600">
            <span className="h-2 w-2 rounded-full bg-emerald-500" /> Honoré
          </span>
          <span className="flex items-center gap-1.5 text-slate-600">
            <span className="h-2 w-2 rounded-full bg-blue-500" /> Confirmé
          </span>
          <span className="flex items-center gap-1.5 text-slate-600">
            <span className="h-2 w-2 rounded-full bg-rose-500" /> No-show
          </span>
          <span className="flex items-center gap-1.5 text-slate-600">
            <span className="h-2 w-2 rounded-full bg-slate-400" /> Annulé
          </span>
        </div>
      </div>

      {/* Grille principale */}
      <div className="grid grid-cols-1 gap-6 lg:grid-cols-12 items-start">
        
        {/* Colonne Gauche : Calendrier */}
        <div className="rounded-2xl border border-slate-200 bg-white shadow-sm overflow-hidden lg:col-span-8">
          
          {/* VUE MOIS */}
          {viewMode === "month" && (
            <div>
              <div className="grid grid-cols-7 border-b border-slate-200 bg-slate-50/80 text-center text-xs font-semibold text-slate-600 py-2.5">
                <span>Lun</span>
                <span>Mar</span>
                <span>Mer</span>
                <span>Jeu</span>
                <span>Ven</span>
                <span>Sam</span>
                <span>Dim</span>
              </div>

              <div className="grid grid-cols-7 divide-x divide-y divide-slate-100">
                {monthGridDays.map((item, idx) => {
                  const dStr = getLocalDateString(item.date);
                  const isToday = dStr === todayStr;
                  const isSelected = selectedDateStr ? dStr === selectedDateStr : isToday;

                  const dayEvents = bookingsByDate[dStr] || [];

                  return (
                    <div
                      key={idx}
                      onClick={() => setSelectedDateStr(dStr)}
                      className={`h-20 sm:h-24 p-1.5 transition-colors cursor-pointer flex flex-col justify-between ${
                        !item.isCurrentMonth
                          ? "bg-slate-50/60 text-slate-400"
                          : isSelected
                          ? "bg-blue-50/60"
                          : "hover:bg-slate-50/80 bg-white"
                      }`}
                    >
                      <div className="flex items-center justify-between">
                        <span
                          className={`flex h-6 w-6 items-center justify-center rounded-full text-xs ${
                            isToday
                              ? "bg-blue-600 text-white font-bold"
                              : isSelected
                              ? "bg-blue-100 text-blue-700 font-bold"
                              : item.isCurrentMonth
                              ? "font-medium text-slate-700"
                              : "text-slate-400"
                          }`}
                        >
                          {item.date.getDate()}
                        </span>
                        {dayEvents.length > 0 && (
                          <span className="text-[10px] font-bold text-slate-400">
                            {dayEvents.length}
                          </span>
                        )}
                      </div>

                      <div className="space-y-1 mt-0.5 overflow-hidden">
                        {dayEvents.slice(0, 2).map((ev) => {
                          const rawTime = ev.start_at || ev.start_time || ev.start;
                          const time = getLocalTimeString(rawTime);
                          const isCompleted = ev.status === "completed";
                          const isNoShow = ev.status === "no_show";
                          const isCancelled = ev.status === "cancelled";

                          const staffTheme = getStaffColor(ev.employee_id || ev.employee_name);

                          return (
                            <div
                              key={ev.id}
                              onClick={(e) => {
                                e.stopPropagation();
                                setSelectedBookingForModal(ev);
                              }}
                              className={`truncate rounded px-1.5 py-0.5 text-[10px] font-medium leading-tight flex items-center gap-1 border transition-all ${
                                isCompleted
                                  ? "bg-emerald-50 text-emerald-800 border-emerald-200"
                                  : isNoShow
                                  ? "bg-rose-50 text-rose-800 border-rose-200 line-through opacity-75"
                                  : isCancelled
                                  ? "bg-slate-100 text-slate-500 border-slate-200 line-through opacity-75"
                                  : `${staffTheme.bg} ${staffTheme.text}${staffTheme.border}`
                              }`}
                            >
                              {!isCancelled && !isNoShow && (
                                <span className={`h-1.5 w-1.5 rounded-full shrink-0 ${staffTheme.dot}`} />
                              )}
                              <span className="font-bold shrink-0">{time}</span>
                              <span className="truncate">
                                {ev.employee_name ? `[${ev.employee_name}] ` : ""}
                                {ev.customer_name || ev.customer?.name || ev.service_name || "RDV"}
                              </span>
                            </div>
                          );
                        })}
                        {dayEvents.length > 2 && (
                          <span className="text-[10px] text-slate-500 font-semibold pl-1">
                            +{dayEvents.length - 2} autre(s)
                          </span>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* VUE SEMAINE */}
          {viewMode === "week" && (
            <div>
              <div className="grid grid-cols-7 border-b border-slate-200 bg-slate-50 text-center py-2 text-xs font-semibold text-slate-700 divide-x divide-slate-200">
                {weekDays.map((d, i) => {
                  const dStr = getLocalDateString(d);
                  const isToday = dStr === todayStr;
                  const isSelected = selectedDateStr === dStr;
                  return (
                    <div
                      key={i}
                      onClick={() => setSelectedDateStr(dStr)}
                      className={`cursor-pointer py-1.5 ${isSelected ? "bg-blue-50 text-blue-700" : ""}`}
                    >
                      <p className="text-[11px] text-slate-500">{d.toLocaleDateString("fr-FR", { weekday: "short" })}</p>
                      <p className={`mt-0.5 inline-flex h-6 w-6 items-center justify-center rounded-full font-bold ${isToday ? "bg-blue-600 text-white" : ""}`}>
                        {d.getDate()}
                      </p>
                    </div>
                  );
                })}
              </div>

              <div className="grid grid-cols-7 divide-x divide-slate-100 min-h-[460px]">
                {weekDays.map((d, i) => {
                  const dStr = getLocalDateString(d);
                  const evs = bookingsByDate[dStr] || [];
                  return (
                    <div
                      key={i}
                      onClick={() => setSelectedDateStr(dStr)}
                      className={`p-2 space-y-1.5 cursor-pointer ${selectedDateStr === dStr ? "bg-blue-50/30" : "hover:bg-slate-50/50"}`}
                    >
                      {evs.map((ev) => (
                        <div
                          key={ev.id}
                          onClick={(e) => {
                            e.stopPropagation();
                            setSelectedBookingForModal(ev);
                          }}
                          className="rounded-lg border border-slate-200 bg-white p-2 shadow-sm text-xs space-y-1 hover:border-blue-400"
                        >
                          <span className="font-bold text-slate-900 text-[11px]">
                            {getLocalTimeString(ev.start_at || ev.start_time || ev.start)}
                          </span>
                          <p className="font-semibold truncate text-slate-800">{ev.service_name || ev.service?.name || "Prestation"}</p>
                          <p className="text-[11px] text-slate-500 truncate">{ev.customer_name || ev.customer?.name}</p>
                        </div>
                      ))}
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* VUE JOUR */}
          {viewMode === "day" && (
            <div className="p-4 space-y-3 min-h-[460px]">
              <div className="flex items-center justify-between pb-3 border-b border-slate-100">
                <span className="font-display text-sm font-bold text-slate-900 capitalize">
                  {currentDate.toLocaleDateString("fr-FR", { weekday: "long", day: "numeric", month: "long" })}
                </span>
                <span className="text-xs font-semibold text-slate-500">
                  {(bookingsByDate[getLocalDateString(currentDate)] || []).length} rendez-vous
                </span>
              </div>

              {(bookingsByDate[getLocalDateString(currentDate)] || []).length === 0 ? (
                <div className="p-12 text-center text-slate-400 text-sm">
                  Aucun créneau programmé pour cette journée.
                </div>
              ) : (
                <div className="divide-y divide-slate-100">
                  {(bookingsByDate[getLocalDateString(currentDate)] || []).map((b) => (
                    <div
                      key={b.id}
                      onClick={() => setSelectedBookingForModal(b)}
                      className="flex items-center justify-between p-3 hover:bg-slate-50 cursor-pointer rounded-lg transition-colors"
                    >
                      <div className="flex items-center gap-3">
                        <span className="font-display font-bold text-sm text-slate-900">
                          {getLocalTimeString(b.start_at || b.start_time || b.start)}
                        </span>
                        <div>
                          <p className="font-semibold text-xs text-slate-900">{b.service_name || b.service?.name || "Prestation"}</p>
                          <p className="text-[11px] text-slate-500">{b.customer_name || b.customer?.name} · {b.customer_phone || b.customer?.phone}</p>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>

        {/* Colonne Droite : Volet Prochains Rendez-vous */}
        <div className="rounded-2xl border border-slate-200 bg-white shadow-sm overflow-hidden lg:col-span-4 flex flex-col">
          <div className="border-b border-slate-100 p-4 bg-slate-50/70">
            <div className="flex items-center justify-between">
              <span className="font-display text-xs font-bold uppercase tracking-wider text-slate-700 flex items-center gap-1.5">
                <CalendarDays className="h-4 w-4 text-blue-600" />
                {selectedDateStr ? "Rendez-vous du jour" : "Prochains rendez-vous"}
              </span>
              {selectedDateStr && (
                <button
                  onClick={() => setSelectedDateStr(null)}
                  className="text-[11px] font-semibold text-blue-600 hover:underline"
                >
                  Voir tous les prochains
                </button>
              )}
            </div>
            <p className="text-xs text-slate-500 mt-1 capitalize">
              {selectedDateStr
                ? new Date(selectedDateStr).toLocaleDateString("fr-FR", { weekday: "long", day: "numeric", month: "long" })
                : "Créneaux à venir (cliquez sur une date pour filtrer)"}
            </p>
          </div>

          <div className="p-4 flex-1 divide-y divide-slate-100 overflow-y-auto max-h-[540px]">
            {sidePanelBookings.length === 0 ? (
              <div className="py-12 text-center">
                <CalendarIcon className="mx-auto h-8 w-8 text-slate-300" />
                <p className="mt-2 text-xs font-semibold text-slate-700">Aucun rendez-vous à afficher</p>
                <p className="text-[11px] text-slate-400 mt-0.5">Naviguez sur le calendrier pour consulter d'autres dates.</p>
              </div>
            ) : (
              sidePanelBookings.map((b) => {
                const rawDate = b.start_at || b.start_time || b.booking_date || b.start;
                const time = getLocalTimeString(rawDate);
                const dayLabel = rawDate ? new Date(rawDate).toLocaleDateString("fr-FR", { day: "numeric", month: "short" }) : "";

                return (
                  <div key={b.id} className="py-3 space-y-2">
                    <div className="flex items-start justify-between gap-2">
                      <div className="min-w-0 flex-1">
                        <div className="flex items-center gap-1.5 flex-wrap">
                          <span className="font-bold text-xs bg-slate-100 text-slate-800 px-1.5 py-0.5 rounded">
                            {dayLabel} à {time}
                          </span>
                          <span className="font-semibold text-xs text-slate-900 truncate">
                            {b.service_name || b.service?.name || "Prestation"}
                          </span>
                        </div>
                        <p className="text-[11px] text-slate-500 truncate mt-1">
                          {b.customer_name || b.customer?.name || "Client"}
                        </p>
                      </div>

                      <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full shrink-0 ${
                        b.status === "completed" 
                          ? "bg-emerald-50 text-emerald-700 border border-emerald-200" 
                          : b.status === "no_show" 
                          ? "bg-rose-50 text-rose-700 border border-rose-200" 
                          : b.status === "cancelled"
                          ? "bg-slate-100 text-slate-500 border border-slate-200" 
                          : "bg-blue-50 text-blue-700 border border-blue-200"
                      }`}>
                        {b.status === "completed" ? "Honoré" : b.status === "no_show" ? "No-show" : b.status === "cancelled" ? "Annulé" : "Confirmé"}
                      </span>
                    </div>

                    <div className="flex items-center gap-1 pt-1 flex-wrap">
                      <Button
                        size="sm"
                        variant="outline"
                        className={`h-7 px-2.5 text-[11px] flex-1 ${
                          b.status === "completed"
                            ? "bg-emerald-600 text-white hover:bg-emerald-700 font-bold"
                            : "text-emerald-700 border-emerald-200 hover:bg-emerald-50"
                        }`}
                        onClick={() => updateBookingStatus(b.id, "completed")}
                      >
                        <CheckCircle2 className="mr-1 h-3 w-3" />
                        {b.status === "completed" ? "Honoré ✓" : "Honoré"}
                      </Button>

                      <Button
                        size="sm"
                        variant="outline"
                        className={`h-7 px-2.5 text-[11px] flex-1 ${
                          b.status === "no_show"
                            ? "bg-rose-600 text-white hover:bg-rose-700 font-bold"
                            : "text-rose-700 border-rose-200 hover:bg-rose-50"
                        }`}
                        onClick={() => updateBookingStatus(b.id, "no_show")}
                      >
                        <XCircle className="mr-1 h-3 w-3" />
                        {b.status === "no_show" ? "No-show ✕" : "No-show"}
                      </Button>

                      {b.status !== "confirmed" && (
                        <Button
                          size="sm"
                          variant="ghost"
                          className="h-7 px-2 text-[11px] text-slate-500 hover:text-slate-800"
                          title="Remettre en attente"
                          onClick={() => updateBookingStatus(b.id, "confirmed")}
                        >
                          <RotateCcw className="h-3 w-3" />
                        </Button>
                      )}
                    </div>
                  </div>
                );
              })
            )}
          </div>
        </div>

      </div>

      <BookingDetailsDialog
        booking={selectedBookingForModal}
        open={Boolean(selectedBookingForModal)}
        onOpenChange={(val) => !val && setSelectedBookingForModal(null)}
        onStatusChange={updateBookingStatus}
      />
    </div>
  );
}